/**
 * Copy + routing helpers for the Google Search paid landing page.
 * Keep this module free of React so unit tests stay cheap and deterministic.
 *
 * Cold Google traffic does not know BotSpot. Visible copy is only:
 * problem headline, plain-English solution, email capture.
 * Badge, bullets, and workflow chrome stay empty so both signup-experience
 * variants show the same words. The A/B test remains look and feel only.
 */

export const GOOGLE_SEARCH_LP_PATH = '/lp/google-search';
export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_EXPERIMENT_NAME = 'google_search_signup_experience_v2';
export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_FEATURE_FLAG = 'google_search_signup_experience_v2_variant';
export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_PREVIEW_PARAM = 'signupExperience';

export const GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS = {
  control: {
    id: 'control',
    contactButtonLabel: 'CONTINUE WITH EMAIL ->',
  },
  auth_first: {
    id: 'auth_first',
  },
};

const EMPTY_AUTH_FIRST = {
  evidenceLabel: '',
  evidenceDescription: '',
  evidenceMeta: '',
  steps: [],
};

export const GOOGLE_SEARCH_VISIBLE_COPY_BAN = (
  /\bTOS\b|stock hacker|command seat|tell botspot|\bmcp\b|pass or fail|for thinkorswim users|schwab trade review|ask once|too many tabs|trade work|should i buy|agentic/i
);

export const GOOGLE_SEARCH_JOBS = {
  'schwab-ai-control': {
    id: 'schwab-ai-control',
    badge: '',
    headline: 'Want AI to keep trading your rules in Schwab?',
    headlineAccent: 'keep trading your rules',
    subhead:
      "Chat tools stop at advice. You still have to place every trade yourself. Set your rules. We'll keep trading them in your Schwab account while you stay in command.",
    proof: [],
    defaultPrompt:
      'I use Schwab. I want agentic trading: set my rules once and keep trading them in my account. I stay in command.',
    authFirst: EMPTY_AUTH_FIRST,
    utmCampaigns: ['b2c_schwab_ai_control_2026q3_r3_daily'],
  },
  'tos-scanner': {
    id: 'tos-scanner',
    badge: '',
    headline: 'Is the thinkorswim scanner too much work?',
    headlineAccent: 'too much work',
    subhead:
      'Use BotSpot AI. Connect your Schwab account, talk in plain English, and the AI builds the scan instead of you clicking through filters.',
    proof: [],
    defaultPrompt:
      'I use thinkorswim. Custom scans hang and thinkScript is too hard. Let me say the scan in English, run it, and explain why each symbol passed',
    authFirst: EMPTY_AUTH_FIRST,
    utmCampaigns: ['b2c_thinkorswim_scanner_2026q3_r3_daily'],
  },
  'portfolio-monitor': {
    id: 'portfolio-monitor',
    badge: '',
    headline: 'Why did a stock you already own just move?',
    headlineAccent: 'just move',
    subhead:
      "You already know the names. You need to know what changed. Connect your Schwab account. We'll explain moves in stocks you already own, with sources, so you are not guessing from a headline.",
    proof: [],
    defaultPrompt:
      'Connect my Schwab portfolio. Tell me why a holding moved, with news, filings, and sources on names I already own',
    authFirst: EMPTY_AUTH_FIRST,
    utmCampaigns: ['b2c_schwab_portfolio_monitor_2026q3_r3_daily'],
  },
  'schwab-copilot': {
    id: 'schwab-copilot',
    badge: '',
    headline: 'Spending hours researching one stock?',
    headlineAccent: 'hours',
    subhead:
      "Pulling charts, news, and numbers by hand takes forever. Tell us the ticker. We'll chart it, read the news and filings, and walk through what changed so you can see it clearly.",
    proof: [],
    defaultPrompt:
      'I use Schwab. I spend hours charting and researching one name. Talk to me like a trading assistant. Chart it, analyze the news and numbers, and help me set up complex options or brackets.',
    authFirst: EMPTY_AUTH_FIRST,
    utmCampaigns: ['b2c_schwab_copilot_2026q3_r3_daily'],
  },
  'schwab-claude-code': {
    id: 'schwab-claude-code',
    badge: '',
    headline: 'Trying to connect Claude Code to your Schwab account?',
    headlineAccent: 'Claude Code',
    subhead:
      'There is no official Schwab hookup for Claude. We host the connection for you. Then Claude Code, Cursor, or Codex can see your positions and place trades you approve, without you running a local server.',
    proof: [],
    defaultPrompt:
      'I want Claude Code to talk to my Schwab account through BotSpot. Help me connect it and pull positions. Skip any local MCP server.',
    authFirst: EMPTY_AUTH_FIRST,
    utmCampaigns: ['b2c_schwab_claude_code_2026q3'],
  },
};

const CAMPAIGN_TO_JOB = Object.fromEntries(
  Object.values(GOOGLE_SEARCH_JOBS).flatMap((job) =>
    (job.utmCampaigns || []).map((campaign) => [campaign, job.id])
  )
);

export function normalizeGoogleSearchSignupExperienceVariant(rawVariant) {
  if (rawVariant && GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS[rawVariant]) {
    return rawVariant;
  }
  return 'control';
}

export function resolveGoogleSearchJobId({ job, utmCampaign, prompt } = {}) {
  const explicit = typeof job === 'string' ? job.trim().toLowerCase() : '';
  if (explicit && GOOGLE_SEARCH_JOBS[explicit]) {
    return explicit;
  }

  const campaign = typeof utmCampaign === 'string' ? utmCampaign.trim() : '';
  if (campaign && CAMPAIGN_TO_JOB[campaign]) {
    return CAMPAIGN_TO_JOB[campaign];
  }

  const promptText = typeof prompt === 'string' ? prompt.toLowerCase() : '';
  if (promptText.includes('scan') || promptText.includes('thinkorswim')) {
    if (promptText.includes('portfolio') || promptText.includes('holding')) {
      return 'portfolio-monitor';
    }
    if (promptText.includes('setup') || promptText.includes('orb') || promptText.includes('vwap')) {
      return promptText.includes('scan') ? 'tos-scanner' : 'schwab-copilot';
    }
    return 'tos-scanner';
  }
  if (promptText.includes('portfolio') || promptText.includes('holding')) {
    return 'portfolio-monitor';
  }
  if (promptText.includes('setup') || promptText.includes('trade review')) {
    return 'schwab-copilot';
  }
  if (promptText.includes('claude') || promptText.includes('codex') || promptText.includes('cursor') || promptText.includes('chatgpt') || promptText.includes('mcp')) {
    return 'schwab-claude-code';
  }

  return 'schwab-ai-control';
}

export function getGoogleSearchJob(jobId) {
  return GOOGLE_SEARCH_JOBS[jobId] || GOOGLE_SEARCH_JOBS['schwab-ai-control'];
}

export function getGoogleSearchSignupExperienceVariantConfig(variantId) {
  return (
    GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS[
      normalizeGoogleSearchSignupExperienceVariant(variantId)
    ] || GOOGLE_SEARCH_SIGNUP_EXPERIENCE_VARIANTS.control
  );
}

export function buildGoogleSearchLpPath({ job, prompt, extraParams } = {}) {
  const params = new URLSearchParams();
  if (job) params.set('job', job);
  if (prompt) params.set('prompt', prompt);
  if (extraParams && typeof extraParams === 'object') {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, String(value));
      }
    });
  }
  const query = params.toString();
  return query ? `${GOOGLE_SEARCH_LP_PATH}?${query}` : GOOGLE_SEARCH_LP_PATH;
}
